import React, { useState } from "react";
import { Stamp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { PhotoUploader } from "./PhotoUploader";
import { CodePlate } from "./BloomBits";

const Input = (props) => (
  <input {...props} className="w-full rounded-full px-5 py-3 bg-warmwhite border border-plum-deep/15 focus:outline-none focus:border-magenta text-sm" />
);

export const StartForm = ({ code, onCreated }) => {
  const [form, setForm] = useState({ plant_name: "", contact_email: "", location: "", planting_date: "" });
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!notes.trim() && photos.length === 0) {
      toast.error("Add a photo or a note so we have something to go on");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/bloom", {
        code,
        ...form,
        entry: { notes, photos, flower_colour: "", growth_habit: "" },
      });
      toast.success("Passport stamped! Your plant is officially on record.");
      onCreated(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create the passport");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="relative bg-cream border-[3px] border-dashed border-magenta/40 rounded-[2rem] p-6 md:p-8" data-testid="bloom-start-form">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="w-10 h-10 rounded-full bg-lemon text-plum-deep flex items-center justify-center"><Stamp className="w-5 h-5" /></span>
        <div>
          <h3 className="font-serif-display uppercase text-plum-deep text-xl tracking-[0.08em]">NEW PASSPORT DETECTED</h3>
          <p className="text-xs text-plum-deep/60">This Bloom ID isn't on record yet — let's stamp its very first page.</p>
        </div>
        <CodePlate code={code} className="ml-auto" />
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Input placeholder="Plant name / best guess (optional)" value={form.plant_name} onChange={(e) => setForm({ ...form, plant_name: e.target.value })} data-testid="bloom-plant-name" />
        <Input type="email" placeholder="Your email (optional)" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} data-testid="bloom-email" />
        <Input placeholder="Where is it planted? e.g. North bed, pot on deck" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="bloom-location" />
        <Input type="date" value={form.planting_date} onChange={(e) => setForm({ ...form, planting_date: e.target.value })} data-testid="bloom-planting-date" title="Planting date" />
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={3}
        placeholder="What are you seeing? Flower colour, petal shape, growth habit, anything useful…"
        data-testid="bloom-first-notes"
        className="mt-4 w-full rounded-3xl px-5 py-3 bg-warmwhite border border-plum-deep/15 focus:outline-none focus:border-magenta text-sm resize-none"
      />
      <div className="mt-4">
        <p className="text-[11px] uppercase tracking-[0.2em] text-plum-deep/60 font-semibold mb-2">PHOTOS OF YOUR PLANT</p>
        <PhotoUploader photos={photos} onChange={setPhotos} />
      </div>
      <button
        type="submit"
        disabled={busy}
        data-testid="bloom-create-btn"
        className="mt-6 inline-flex items-center gap-3 rounded-full bg-magenta text-cream px-8 py-4 font-serif-display uppercase text-sm tracking-[0.18em] font-semibold hover:bg-plum transition-colors disabled:opacity-50"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Stamp className="w-4 h-4" />}
        STAMP MY PASSPORT
      </button>
    </form>
  );
};
